import React from 'react';
import { SearchOutlined, FilterOutlined, PlusOutlined } from '@ant-design/icons';
import AppModal from './AppModal';
import { Form, Input, InputNumber, Select as AntSelect } from 'antd';
import { Container } from '@/components/ui/Container';
import Table from '@/components/ui/table/Table';
import { SliderTab } from '@/components/ui/SliderTab';
import { Button } from '@/components/ui/Button';
import SidePanel from '@/components/ui/SidePanel';
import SalesLeadDetailPanel from './SalesLeadDetailPanel';
import { useSalesLeads } from '@hooks/sales/useSalesLeads';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    RolesSliderTabWrap,
    RolesSearchWrapper,
    RolesSearchInput,
    RolesFilterBtn,
    ErrorBlock,
    CreateRoleModalBody,
    CreateRoleModalFooter,
} from './style';

const Sales = () => {
    const [createProjectForm] = Form.useForm();
    const {
        total,
        activeCount,
        qualifiedCount,
        closedCount,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        panelOpen,
        closePanel,
        openPanel,
        selectedLeadId,
        selectedLeadName,
        canView,
        canCreate,
        leadsError,
        createLeadModalOpen,
        handleOpenCreateLeadModal,
        handleCloseCreateLeadModal,
        handleSubmitCreateLead,
        createLeadForm,
        createLeadMutation,
        createProjectModalOpen,
        createProjectLead,
        handleCloseCreateProjectModal,
        handleCreateProjectFromLead,
        createProjectMutation,
    } = useSalesLeads();

    return (
        <ListBodyLayout
            title="Leads"
            description="Manage sales leads and pipeline"
            actions={
                <Button
                    $primary
                    $blackOnLight
                    icon={<PlusOutlined />}
                    onClick={handleOpenCreateLeadModal}
                    permissionDenied={!canCreate}
                    $padding="10px 20px"
                    $size="14px"
                >
                    Create Lead
                </Button>
            }
            kpiCards={[
                {
                    title: 'Total Leads',
                    value: total,
                    change: { value: '', isPositive: true, text: 'in pipeline' },
                },
                {
                    title: 'Active',
                    value: activeCount,
                    change: { value: '', isPositive: true, text: 'Active leads' },
                },
                {
                    title: 'Qualified',
                    value: qualifiedCount,
                    change: { value: '', isPositive: true, text: 'Qualified' },
                },
                {
                    title: 'Closed',
                    value: closedCount,
                    change: { value: '', isPositive: true, text: 'Won / Lost' },
                },
            ]}
        >
            {leadsError && (
                <ErrorBlock>
                    Unable to load leads. Check that the Sales API (GET /api/v1/sales/leads) is available.
                </ErrorBlock>
            )}

            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="center"
                $flexWrap="wrap"
                $gap="12px"
                $marginBottom="16px"
            >
                <Container $display="flex" $alignItems="center" $minWidth="0">
                    <RolesSliderTabWrap>
                        <SliderTab
                            items={[
                                { key: STATUS_TAB.ALL, label: 'All' },
                                { key: STATUS_TAB.ACTIVE, label: 'Active' },
                                { key: STATUS_TAB.QUALIFIED, label: 'Qualified' },
                                { key: STATUS_TAB.DISQUALIFIED, label: 'Disqualified' },
                                { key: STATUS_TAB.CLOSED, label: 'Closed' },
                            ]}
                            activeKey={statusTab}
                            onChange={setStatusTab}
                            fitContent
                        />
                    </RolesSliderTabWrap>
                </Container>
                <Container $display="flex" $alignItems="center" $gap="10px">
                    <RolesSearchWrapper>
                        <SearchOutlined />
                        <RolesSearchInput
                            placeholder="Search here"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </RolesSearchWrapper>
                    <RolesFilterBtn type="button">
                        <FilterOutlined />
                        Filter
                    </RolesFilterBtn>
                </Container>
            </Container>

            <Table
                title=""
                columns={columns}
                dataSource={tableData}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showSearch={false}
                showFilter={false}
                showMore={false}
                hideToolbar
                pagination
                pageSize={10}
                $topMargin={false}
                $radius={12}
                onRow={(record) => ({
                    onClick: () => canView && openPanel(record.id),
                    style: { cursor: canView ? 'pointer' : 'default' },
                })}
            />

            <SidePanel open={panelOpen} onClose={closePanel} title={selectedLeadName} width="480px">
                <SalesLeadDetailPanel leadId={selectedLeadId} />
            </SidePanel>

            <AppModal
                size="md"
                title="Create Lead"
                open={createLeadModalOpen}
                onCancel={handleCloseCreateLeadModal}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form form={createLeadForm} layout="vertical" initialValues={{ source: 'manual', status: 'active', stage: 'new' }}>
                        <Form.Item name="name" label="Client Name">
                            <Input placeholder="John Doe" size="large" />
                        </Form.Item>
                        <Form.Item
                            name="email"
                            label="Client Email"
                            rules={[{ required: true, message: 'Enter client email' }, { type: 'email' }]}
                        >
                            <Input placeholder="lead@example.com" size="large" />
                        </Form.Item>
                        <Form.Item name="phone" label="Client Phone">
                            <Input placeholder="+1234567890" size="large" />
                        </Form.Item>
                        <Form.Item name="company" label="Client Company">
                            <Input placeholder="Acme Corp" size="large" />
                        </Form.Item>
                        <Form.Item name="estimatedValue" label="Estimated value">
                            <InputNumber
                                placeholder="e.g. 50000"
                                size="large"
                                min={0}
                                style={{ width: '100%' }}
                                parser={(v) => (v === '' || v == null ? undefined : Number(String(v).replace(/\D/g, '')))}
                                formatter={(v) => (v == null ? '' : Number(v).toLocaleString())}
                            />
                        </Form.Item>
                        <Form.Item name="notes" label="Notes">
                            <Input.TextArea rows={3} placeholder="Interested in Pro plan" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button $primary={false} onClick={handleCloseCreateLeadModal} $padding="10px 16px" $size="14px">
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $padding="10px 16px"
                        $size="14px"
                        loading={createLeadMutation.isPending}
                        onClick={handleSubmitCreateLead}
                        permissionDenied={!canCreate}
                    >
                        Create
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>

            <AppModal
                size="md"
                title="Create Project"
                open={createProjectModalOpen}
                onCancel={() => {
                    handleCloseCreateProjectModal();
                    createProjectForm.resetFields();
                }}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form
                        form={createProjectForm}
                        layout="vertical"
                        initialValues={{
                            name:
                                createProjectLead?.name ||
                                createProjectLead?.company ||
                                createProjectLead?.email ||
                                '',
                        }}
                    >
                        {createProjectLead && (
                            <Container $display="flex" $flexDirection="column" $gap="10px" $marginBottom="10px">
                                <Form.Item label="Client Name" style={{ marginBottom: 0 }}>
                                    <Input value={createProjectLead.name || ''} placeholder="Client Name" size="large" disabled />
                                </Form.Item>
                                <Form.Item label="Client Email" style={{ marginBottom: 0 }}>
                                    <Input value={createProjectLead.email || ''} placeholder="Client Email" size="large" disabled />
                                </Form.Item>
                                <Form.Item label="Client Phone" style={{ marginBottom: 0 }}>
                                    <Input value={createProjectLead.phone || ''} placeholder="Client Phone" size="large" disabled />
                                </Form.Item>
                                <Form.Item label="Client Company" style={{ marginBottom: 0 }}>
                                    <Input value={createProjectLead.company || ''} placeholder="Client Company" size="large" disabled />
                                </Form.Item>
                            </Container>
                        )}
                        <Form.Item
                            name="name"
                            label="Project name"
                            rules={[{ required: true, message: 'Enter project name' }]}
                        >
                            <Input placeholder="e.g. Website Redesign" size="large" />
                        </Form.Item>
                        <Form.Item name="budget" label="Budget">
                            <InputNumber
                                placeholder="e.g. 50000"
                                size="large"
                                min={0}
                                style={{ width: '100%' }}
                                parser={(v) =>
                                    v === '' || v == null ? undefined : Number(String(v).replace(/\D/g, ''))
                                }
                                formatter={(v) => (v == null ? '' : Number(v).toLocaleString())}
                            />
                        </Form.Item>
                        <Form.Item
                            name="priority"
                            label="Priority"
                            rules={[{ required: true, message: 'Select priority' }]}
                        >
                            <AntSelect
                                placeholder="Select priority"
                                size="large"
                                options={[
                                    { value: 'high', label: 'High' },
                                    { value: 'medium', label: 'Medium' },
                                    { value: 'low', label: 'Low' },
                                ]}
                            />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button
                        $primary={false}
                        onClick={() => {
                            handleCloseCreateProjectModal();
                            createProjectForm.resetFields();
                        }}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $padding="10px 16px"
                        $size="14px"
                        loading={createProjectMutation.isPending}
                        onClick={() => {
                            createProjectForm.validateFields().then((values) => {
                                handleCreateProjectFromLead(values);
                            });
                        }}
                    >
                        Create Project
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>
        </ListBodyLayout>
    );
};

export default Sales;
